import { useCallback, useEffect, useState } from 'react';
import { Bot, Check, FlaskConical, KeyRound, Pencil, Plus, X } from 'lucide-react';
import { useQueue } from '../../context/QueueContext';

const API = process.env.REACT_APP_BACKEND_URL;

const labelCls = 'block text-xs font-semibold uppercase tracking-wide text-navy';
const inputCls =
  'mt-1 h-12 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none focus:border-navy focus:ring-2 focus:ring-navy/15';

// Catálogo curado pelo sistema (somente modelos testados). O botão "Buscar da minha
// conta" acrescenta às opções os modelos que a chave cadastrada realmente acessa.
const MODEL_CATALOG = {
  openai: {
    text: ['gpt-5.5', 'gpt-5.4', 'gpt-5.4-mini'],
    vision: ['gpt-5.5', 'gpt-5.4'],
    image: [
      { id: 'gpt-image-2.5-flare', label: 'gpt-image-2.5-flare — rápido e econômico (padrão)' },
      { id: 'gpt-image-2.5-sunburst', label: 'gpt-image-2.5-sunburst — máxima qualidade / edição precisa' },
      { id: 'gpt-image-2', label: 'gpt-image-2 — geração anterior' },
    ],
  },
  gemini: {
    text: ['gemini-3.1-pro', 'gemini-3-flash', 'gemini-2.5-flash'],
    vision: ['gemini-3.1-pro', 'gemini-3-flash'],
    image: [
      { id: 'gemini-3.1-flash-image', label: 'gemini-3.1-flash-image (Nano Banana 2) — uso geral' },
      { id: 'gemini-3-pro-image', label: 'gemini-3-pro-image (Nano Banana Pro) — melhor texto na imagem' },
      { id: 'gemini-3.1-flash-lite-image', label: 'gemini-3.1-flash-lite-image — rápido e econômico' },
    ],
  },
};

const PRESETS = [
  { providerKey: 'gemini', name: 'Google Gemini', visionModel: 'gemini-3.1-pro', textModel: 'gemini-3.1-pro', imageModel: 'gemini-3.1-flash-image' },
  { providerKey: 'openai', name: 'OpenAI', visionModel: 'gpt-5.4', textModel: 'gpt-5.4', imageModel: 'gpt-image-2.5-flare' },
  { providerKey: 'outro', name: '', visionModel: '', textModel: '', imageModel: '' },
];

const EMPTY_FORM = { id: null, name: '', providerKey: 'gemini', apiKey: '', visionModel: '', textModel: '', imageModel: '', active: false };

// Integrações de IA (admin-only). As API keys vão SOMENTE para o backend (MongoDB);
// o frontend nunca recebe a chave completa — só "Configurado" + últimos 4 caracteres.
// Nada é persistido em localStorage.
export default function AdminAIIntegrations() {
  const { user } = useQueue();
  const isAdmin = user?.role === 'admin';
  const headers = { 'Content-Type': 'application/json', 'X-Admin-Role': user?.role || '' };

  const [providers, setProviders] = useState([]);
  const [settings, setSettings] = useState({ imageProviderId: '', textProviderId: '', promptProductImage: '', promptCategoryImage: '' });
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');
  const [testResults, setTestResults] = useState({});
  const [accountModels, setAccountModels] = useState(null);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        fetch(`${API}/api/ai/providers`, { headers }).then((r) => (r.ok ? r.json() : [])),
        fetch(`${API}/api/ai/settings`, { headers }).then((r) => (r.ok ? r.json() : {})),
      ]);
      setProviders(p);
      setSettings({
        imageProviderId: s.imageProviderId || '',
        textProviderId: s.textProviderId || '',
        promptProductImage: s.promptProductImage || '',
        promptCategoryImage: s.promptCategoryImage || '',
      });
    } catch {
      setError('Não foi possível carregar as integrações.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center" data-testid="ai-integrations-denied">
        <p className="font-serif text-2xl text-navy">Acesso restrito</p>
        <p className="mt-2 text-sm text-muted-foreground">Esta área é exclusiva do administrador.</p>
      </div>
    );
  }

  const notify = (msg) => {
    setFlash(msg);
    setTimeout(() => setFlash(''), 4000);
  };

  const startCreate = (preset) => {
    setForm({ ...EMPTY_FORM, ...preset });
    setAccountModels(null);
  };
  const startEdit = (p) => {
    setForm({ id: p.id, name: p.name, providerKey: p.providerKey, apiKey: '', visionModel: p.visionModel, textModel: p.textModel, imageModel: p.imageModel || '', active: p.active });
    setAccountModels(null);
  };

  // Busca real dos modelos que a chave cadastrada acessa (backend consulta o provedor).
  const fetchModels = async () => {
    if (!form?.id) return;
    setModelsLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/ai/providers/${form.id}/models`, { headers });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(d.detail || 'Não foi possível consultar os modelos da conta.');
        return;
      }
      setAccountModels(d);
      notify('Modelos da sua conta carregados nos seletores.');
    } catch {
      setError('Sem conexão com o backend.');
    } finally {
      setModelsLoading(false);
    }
  };

  // Opções de cada seletor: catálogo curado + modelos da conta + valor atual (se fora da lista).
  const modelOptions = (kind) => {
    if (!form) return [];
    const curated = (MODEL_CATALOG[form.providerKey] || {})[kind] || [];
    const opts = curated.map((o) => (typeof o === 'string' ? { id: o, label: o } : o));
    (accountModels?.[kind] || []).filter((m) => !opts.some((o) => o.id === m)).forEach((m) => opts.push({ id: m, label: `${m} (da sua conta)` }));
    const current = kind === 'text' ? form.textModel : kind === 'vision' ? form.visionModel : form.imageModel;
    if (current && !opts.some((o) => o.id === current)) opts.unshift({ id: current, label: `${current} (atual)` });
    return opts;
  };

  const saveProvider = async () => {
    setError('');
    if (!form.name.trim()) {
      setError('Informe o nome do provedor.');
      return;
    }
    if (!form.id && !form.apiKey.trim()) {
      setError('Informe a API key do provedor.');
      return;
    }
    const url = form.id ? `${API}/api/ai/providers/${form.id}` : `${API}/api/ai/providers`;
    const res = await fetch(url, { method: form.id ? 'PUT' : 'POST', headers, body: JSON.stringify(form) });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setError(d.detail || 'Não foi possível salvar o provedor.');
      return;
    }
    setForm(null);
    notify(form.id ? 'Provedor atualizado.' : 'Provedor configurado com segurança no backend.');
    load();
  };

  const testProvider = async (p) => {
    setTestResults((t) => ({ ...t, [p.id]: { testing: true } }));
    try {
      const res = await fetch(`${API}/api/ai/providers/${p.id}/test`, { method: 'POST', headers });
      const d = await res.json().catch(() => ({}));
      setTestResults((t) => ({ ...t, [p.id]: res.ok ? { ok: true, message: d.message } : { ok: false, message: d.detail || 'Falha no teste.' } }));
    } catch {
      setTestResults((t) => ({ ...t, [p.id]: { ok: false, message: 'Sem conexão com o backend.' } }));
    }
  };

  const saveDefaults = async () => {
    setError('');
    const res = await fetch(`${API}/api/ai/settings`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({
        imageProviderId: settings.imageProviderId || null,
        textProviderId: settings.textProviderId || null,
        promptProductImage: settings.promptProductImage,
        promptCategoryImage: settings.promptCategoryImage,
      }),
    });
    if (!res.ok) {
      setError('Não foi possível salvar os provedores padrão.');
      return;
    }
    notify('Provedores padrão salvos.');
  };

  const savePrompts = async () => {
    setError('');
    const res = await fetch(`${API}/api/ai/settings`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({
        imageProviderId: settings.imageProviderId || null,
        textProviderId: settings.textProviderId || null,
        promptProductImage: settings.promptProductImage,
        promptCategoryImage: settings.promptCategoryImage,
      }),
    });
    if (!res.ok) {
      setError('Não foi possível salvar os prompts de imagem.');
      return;
    }
    notify('Prompts de imagem salvos no backend.');
  };

  const activeProviders = providers.filter((p) => p.active);

  return (
    <div className="mx-auto max-w-3xl" data-testid="ai-integrations-page">
      <h2 className="font-serif text-2xl text-navy">Integrações de IA</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Configure os provedores que serão usados no cadastro de produtos: análise de fotos, identificação de
        variantes/cores e geração de descrições. As chaves são enviadas ao backend e armazenadas com segurança — o
        frontend nunca recebe a chave completa.
      </p>

      {error && (
        <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700" role="alert" data-testid="ai-form-error">{error}</p>
      )}
      {flash && (
        <p className="mt-4 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800" data-testid="ai-flash">{flash}</p>
      )}

      <section className="mt-6 rounded-2xl border border-navy/10 bg-white p-5" data-testid="ai-defaults-section">
        <h3 className="font-serif text-lg text-navy">Provedores padrão</h3>
        <p className="mt-1 text-xs text-muted-foreground">Somente provedores ativos aparecem como opção.</p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="ai-default-image" className={labelCls}>Análise de imagens</label>
            <select
              id="ai-default-image"
              data-testid="ai-default-image-select"
              value={settings.imageProviderId}
              onChange={(e) => setSettings((s) => ({ ...s, imageProviderId: e.target.value }))}
              className={inputCls}
            >
              <option value="">Simulado (padrão do protótipo)</option>
              {activeProviders.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="ai-default-text" className={labelCls}>Geração de textos/descrições</label>
            <select
              id="ai-default-text"
              data-testid="ai-default-text-select"
              value={settings.textProviderId}
              onChange={(e) => setSettings((s) => ({ ...s, textProviderId: e.target.value }))}
              className={inputCls}
            >
              <option value="">Simulado (padrão do protótipo)</option>
              {activeProviders.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>
        <button
          type="button"
          onClick={saveDefaults}
          data-testid="ai-defaults-save"
          className="mt-4 flex h-12 items-center justify-center gap-2 rounded-xl bg-navy px-6 text-sm font-semibold text-white hover:bg-navy-hover"
        >
          <Check className="h-4 w-4" /> SALVAR PADRÕES
        </button>
      </section>

      <section className="mt-6 rounded-2xl border border-navy/10 bg-white p-5" data-testid="ai-prompts-section">
        <h3 className="font-serif text-lg text-navy">Padrão de imagem por IA</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Prompts-base das gerações de imagem — configuração da aplicação Huddersfield, separada dos dados do provedor
          (nome/API key/modelos). Os marcadores {'{categoria}'}/{'{descricao}'} e {'{tecido}'}/{'{variante}'} são substituídos
          automaticamente. Campo vazio usa o prompt padrão aprovado. Salvos no backend (nunca em localStorage).
        </p>
        <div className="mt-4">
          <label htmlFor="ai-prompt-product" className={labelCls}>Prompt de imagem principal de produto</label>
          <textarea
            id="ai-prompt-product"
            data-testid="ai-prompt-product"
            value={settings.promptProductImage}
            onChange={(e) => setSettings((s) => ({ ...s, promptProductImage: e.target.value }))}
            rows={4}
            className="mt-1 w-full rounded-xl border border-input bg-white px-4 py-3 text-xs outline-none focus:border-navy"
          />
        </div>
        <div className="mt-4">
          <label htmlFor="ai-prompt-category" className={labelCls}>Prompt de imagem de categoria</label>
          <textarea
            id="ai-prompt-category"
            data-testid="ai-prompt-category"
            value={settings.promptCategoryImage}
            onChange={(e) => setSettings((s) => ({ ...s, promptCategoryImage: e.target.value }))}
            rows={6}
            className="mt-1 w-full rounded-xl border border-input bg-white px-4 py-3 text-xs outline-none focus:border-navy"
          />
        </div>
        <button
          type="button"
          onClick={savePrompts}
          data-testid="ai-prompts-save"
          className="mt-4 flex h-12 items-center justify-center gap-2 rounded-xl bg-navy px-6 text-sm font-semibold text-white hover:bg-navy-hover"
        >
          <Check className="h-4 w-4" /> SALVAR PROMPTS
        </button>
      </section>

      <section className="mt-6" data-testid="ai-providers-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-serif text-lg text-navy">Provedores</h3>
          <div className="flex gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.providerKey}
                type="button"
                onClick={() => startCreate(p)}
                data-testid={`ai-add-${p.providerKey}`}
                className="flex h-10 items-center gap-1.5 rounded-full border border-navy/25 px-4 text-xs font-semibold text-navy hover:bg-navy-light"
              >
                <Plus className="h-3.5 w-3.5" /> {p.providerKey === 'outro' ? 'OUTRO' : p.name.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {form && (
          <div className="mt-4 rounded-2xl border-2 border-navy/20 bg-navy-light/30 p-5" data-testid="ai-provider-form">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-navy">{form.id ? 'Editar provedor' : 'Novo provedor'}</h4>
              <button type="button" onClick={() => setForm(null)} data-testid="ai-form-cancel" aria-label="Fechar formulário" className="rounded-lg p-2 text-navy hover:bg-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="ai-name" className={labelCls}>Nome do provedor *</label>
                <input id="ai-name" data-testid="ai-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Ex.: Google Gemini" />
              </div>
              <div>
                <label htmlFor="ai-key" className={labelCls}>API key / token {form.id ? '' : '*'}</label>
                <input
                  id="ai-key"
                  type="password"
                  autoComplete="new-password"
                  data-testid="ai-form-key"
                  value={form.apiKey}
                  onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                  className={inputCls}
                  placeholder={form.id ? 'Deixe em branco para manter a chave atual' : 'Cole a chave do provedor'}
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  A chave vai direto para o backend e nunca volta para o navegador.
                </p>
              </div>
              {form.providerKey === 'outro' ? (
                <>
                  <div>
                    <label htmlFor="ai-text-model" className={labelCls}>Modelo de texto</label>
                    <input id="ai-text-model" data-testid="ai-form-text-model" value={form.textModel} onChange={(e) => setForm({ ...form, textModel: e.target.value })} className={inputCls} placeholder="Ex.: gpt-5.4" />
                  </div>
                  <div>
                    <label htmlFor="ai-vision-model" className={labelCls}>Modelo de visão/análise</label>
                    <input id="ai-vision-model" data-testid="ai-form-vision-model" value={form.visionModel} onChange={(e) => setForm({ ...form, visionModel: e.target.value })} className={inputCls} placeholder="Ex.: gemini-3.1-pro" />
                  </div>
                  <div>
                    <label htmlFor="ai-image-model" className={labelCls}>Modelo de geração de imagem</label>
                    <input id="ai-image-model" data-testid="ai-form-image-model" value={form.imageModel} onChange={(e) => setForm({ ...form, imageModel: e.target.value })} className={inputCls} placeholder="Ex.: gpt-image-2.5-flare" />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label htmlFor="ai-text-model" className={labelCls}>Modelo de texto (ficha técnica, descrições)</label>
                    <select id="ai-text-model" data-testid="ai-form-text-model" value={form.textModel} onChange={(e) => setForm({ ...form, textModel: e.target.value })} className={inputCls}>
                      <option value="">Padrão do sistema</option>
                      {modelOptions('text').map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="ai-vision-model" className={labelCls}>Modelo de visão/análise (fotos)</label>
                    <select id="ai-vision-model" data-testid="ai-form-vision-model" value={form.visionModel} onChange={(e) => setForm({ ...form, visionModel: e.target.value })} className={inputCls}>
                      <option value="">Padrão do sistema</option>
                      {modelOptions('vision').map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="ai-image-model" className={labelCls}>Modelo de geração de imagem (capa, categorias)</label>
                    <select id="ai-image-model" data-testid="ai-form-image-model" value={form.imageModel} onChange={(e) => setForm({ ...form, imageModel: e.target.value })} className={inputCls}>
                      <option value="">Padrão do sistema</option>
                      {modelOptions('image').map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={fetchModels}
                      disabled={!form.id || modelsLoading}
                      data-testid="ai-form-fetch-models"
                      className="flex h-12 items-center gap-2 rounded-xl border border-navy/25 px-4 text-xs font-semibold text-navy hover:bg-navy-light disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {modelsLoading ? 'BUSCANDO…' : 'BUSCAR MODELOS DA MINHA CONTA'}
                    </button>
                  </div>
                  {!form.id && (
                    <p className="text-[11px] text-muted-foreground sm:col-span-2">Salve o provedor para poder buscar os modelos disponíveis na sua conta.</p>
                  )}
                </>
              )}
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setForm({ ...form, active: !form.active })}
                aria-pressed={form.active}
                data-testid="ai-form-active"
                className={`rounded-full border px-4 py-2.5 text-xs font-semibold transition-colors ${
                  form.active ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-navy/20 text-foreground/70 hover:border-navy'
                }`}
              >
                {form.active ? 'ATIVO' : 'INATIVO'}
              </button>
              <button
                type="button"
                onClick={saveProvider}
                data-testid="ai-form-save"
                className="flex h-12 items-center gap-2 rounded-xl bg-navy px-6 text-sm font-semibold text-white hover:bg-navy-hover"
              >
                <Check className="h-4 w-4" /> SALVAR PROVEDOR
              </button>
            </div>
          </div>
        )}

        <div className="mt-4 space-y-3" data-testid="ai-providers-list">
          {!loading && providers.length === 0 && (
            <p className="rounded-2xl border border-dashed border-navy/20 p-8 text-center text-sm text-muted-foreground" data-testid="ai-providers-empty">
              Nenhum provedor configurado. O cadastro de produtos continua usando a simulação local.
            </p>
          )}
          {providers.map((p) => (
            <div key={p.id} className="rounded-2xl border border-navy/10 bg-white p-4" data-testid={`ai-provider-card-${p.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy-light">
                    <Bot className="h-5 w-5 text-navy" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-navy">{p.name}</p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1" data-testid={`ai-provider-status-${p.id}`}>
                        <KeyRound className="h-3 w-3" />
                        {p.keyConfigured ? `Configurado ••••${p.keyLast4}` : 'Chave não configurada'}
                      </span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${p.active ? 'bg-emerald-100 text-emerald-800' : 'bg-navy/10 text-navy/60'}`} data-testid={`ai-provider-active-${p.id}`}>
                        {p.active ? 'ATIVO' : 'INATIVO'}
                      </span>
                    </p>
                    {(p.visionModel || p.textModel || p.imageModel) && (
                      <p className="mt-1 text-[11px] text-muted-foreground" data-testid={`ai-provider-models-${p.id}`}>
                        {p.textModel ? `Texto: ${p.textModel}` : ''}{p.textModel && (p.visionModel || p.imageModel) ? ' · ' : ''}
                        {p.visionModel ? `Visão: ${p.visionModel}` : ''}{p.visionModel && p.imageModel ? ' · ' : ''}
                        {p.imageModel ? `Imagem: ${p.imageModel}` : ''}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => testProvider(p)}
                    data-testid={`ai-provider-test-${p.id}`}
                    className="flex h-10 items-center gap-1.5 rounded-xl border border-navy/25 px-4 text-xs font-semibold text-navy hover:bg-navy-light"
                  >
                    <FlaskConical className="h-3.5 w-3.5" /> TESTAR CONEXÃO
                  </button>
                  <button
                    type="button"
                    onClick={() => startEdit(p)}
                    data-testid={`ai-provider-edit-${p.id}`}
                    className="flex h-10 items-center gap-1.5 rounded-xl border border-navy/25 px-4 text-xs font-semibold text-navy hover:bg-navy-light"
                  >
                    <Pencil className="h-3.5 w-3.5" /> EDITAR
                  </button>
                </div>
              </div>
              {testResults[p.id] && !testResults[p.id].testing && (
                <p
                  className={`mt-3 rounded-xl px-4 py-2.5 text-xs font-medium ${testResults[p.id].ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'}`}
                  data-testid={`ai-test-result-${p.id}`}
                >
                  {testResults[p.id].message}
                </p>
              )}
              {testResults[p.id]?.testing && <p className="mt-3 text-xs text-muted-foreground">Testando…</p>}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
